/**
 * Turning a file somebody dropped into a SourceRef, and getting it back later.
 *
 * We keep no media bytes, so the only durable thing about a file is its
 * description. Two jobs live here: reading that description out of a real file,
 * and deciding whether a file offered later is the one an edit was built on.
 */

import type { SourceRef } from "./timeline.ts";
import { newId, STILL_DURATION } from "./timeline.ts";

/** What the pickers and drop zones accept. One string, used everywhere. */
export const ACCEPT = "video/*,audio/*,image/*";

const IMAGE_EXTENSIONS = /\.(png|jpe?g|webp|gif|avif|bmp|svg)$/i;

/** Whether a file is a picture, by type first and then by name, since a drop can arrive with no type. */
export const isImageFile = (file: File): boolean =>
  file.type.startsWith("image/") || (!file.type && IMAGE_EXTENSIONS.test(file.name));

/** A file we currently hold, alongside the reference that describes it. */
export interface ResolvedSource {
  ref: SourceRef;
  file: File;
  /** Object URL for the media elements. Revoked when the source is dropped. */
  url: string;
  /** Present only where the browser gave us one that survives a reload. */
  handle?: FileSystemFileHandle;
}

const HASH_WINDOW = 1024 * 1024;

/**
 * Size, plus a hash of the first and last megabyte.
 *
 * Hashing a whole 2 GB video takes ten seconds or more and would happen on
 * every import. The head and tail together catch a re-encode or a different
 * cut while costing two reads, and the size in front makes a collision between
 * two files a person would plausibly confuse effectively impossible.
 */
export async function fingerprintFile(file: File): Promise<string> {
  const head = file.slice(0, Math.min(HASH_WINDOW, file.size));
  const tail = file.slice(Math.max(0, file.size - HASH_WINDOW));
  const bytes = new Uint8Array(
    (await head.arrayBuffer()).byteLength + (await tail.arrayBuffer()).byteLength,
  );
  bytes.set(new Uint8Array(await head.arrayBuffer()), 0);
  bytes.set(new Uint8Array(await tail.arrayBuffer()), new Uint8Array(await head.arrayBuffer()).length);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${file.size}-${hex.slice(0, 32)}`;
}

export type MatchQuality = "exact" | "changed" | "different";

/**
 * How much this file looks like the one the project used.
 *
 * Nothing here refuses a file. A person re-exporting their footage and handing
 * us the new version is doing something reasonable, and they know more about
 * their files than a hash does; the caller shows the warning and lets them
 * decide.
 */
export function matchQuality(ref: SourceRef, fingerprint: string, file: File): MatchQuality {
  if (fingerprint === ref.fingerprint) return "exact";
  if (file.name === ref.name) return "changed";
  return "different";
}

export const matchWarning = (quality: MatchQuality, ref: SourceRef): string | null => {
  if (quality === "exact") return null;
  if (quality === "changed") {
    return `This looks like a different version of ${ref.name}. Using it anyway.`;
  }
  return `This doesn't look like ${ref.name}, which is what the project was built on. Using it anyway.`;
};

/**
 * Read a file's shape by letting the browser open it.
 *
 * Everything here comes from a media element rather than from parsing the
 * container ourselves, which is the whole reason this app has no demuxer in it.
 * A file the browser cannot open has no duration, and that is the honest
 * moment to say so rather than halfway through an export.
 */
export async function probeFile(file: File): Promise<SourceRef> {
  if (isImageFile(file)) return probeImage(file);
  const url = URL.createObjectURL(file);
  try {
    const { probe, element } = await loadMetadata(url);
    if (!Number.isFinite(probe.duration) || probe.duration <= 0) {
      throw new Error(`${file.name} has no readable duration. Your browser may not support this format.`);
    }
    const hasAudio = await probeAudio(file, element);
    element.remove();
    return {
      id: newId("s"),
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      duration: probe.duration,
      ...(probe.width ? { width: probe.width, height: probe.height } : {}),
      hasVideo: probe.hasVideo,
      hasAudio,
      fingerprint: await fingerprintFile(file),
      ...(probe.poster ? { poster: probe.poster } : {}),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface Probe {
  duration: number;
  width: number;
  height: number;
  hasVideo: boolean;
  poster?: string;
}

/**
 * A picture is a video source with no clock: it has a size and no duration of
 * its own, and it goes on a video lane like anything else with pixels.
 *
 * Decoding through an <img> rather than createImageBitmap because it is what
 * the pool draws with later, so a file the one can open the other can too.
 * Transparency comes through untouched: the canvas composites a PNG's alpha
 * over whatever is beneath it, which is the whole point of a logo overlay.
 */
async function probeImage(file: File): Promise<SourceRef> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    if (image.naturalWidth === 0 || image.naturalHeight === 0) {
      throw new Error(`${file.name} has no size your browser can read.`);
    }
    return {
      id: newId("s"),
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      duration: STILL_DURATION,
      width: image.naturalWidth,
      height: image.naturalHeight,
      hasVideo: true,
      hasAudio: false,
      still: true,
      fingerprint: await fingerprintFile(file),
      ...(posterOf(image) ? { poster: posterOf(image) } : {}),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Your browser could not open this picture. Try PNG, JPEG, WebP or GIF."));
    image.src = url;
  });
}

function posterOf(image: HTMLImageElement): string | undefined {
  try {
    const scale = Math.min(1, POSTER_WIDTH / image.naturalWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    // On the mat's colour, so a transparent logo is not a thumbnail of nothing.
    ctx.fillStyle = "#14100c";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return undefined;
  }
}

/**
 * Above this, the audio probe is skipped and audio is assumed present.
 *
 * The probe has to read the whole file to decode its audio, so on a very large
 * one the transient cost is the file itself in memory. Assuming audio is the
 * conservative wrong answer: an extra track someone can delete, rather than
 * sound silently missing from their edit.
 */
const AUDIO_PROBE_MAX_BYTES = 250 * 1024 * 1024;

/** Peak sample below which a track is treated as having no sound in it. */
const SILENCE_PEAK = 0.0005;

/**
 * Does this file have sound worth putting on a track?
 *
 * There is no cheap, reliable answer in every browser, and this was measured
 * rather than assumed (see dev/audio-probe.html). Chrome exposes neither
 * `mozHasAudio` nor `audioTracks`, and `webkitAudioDecodedByteCount` sits at 0
 * whether or not a track exists. What does work everywhere is asking the
 * browser to decode the audio: it throws for a file that has none.
 *
 * Decoding also answers the second half of the question. A track of pure
 * silence — a camera with the mic off, a screen recording with nothing playing
 * — is worse than no track, because it takes up a lane and suggests there is
 * something to hear. So the samples are checked for signal too.
 */
async function probeAudio(file: File, element: HTMLVideoElement): Promise<boolean> {
  const hinted = element as AudioProbe;
  if (typeof hinted.mozHasAudio === "boolean") return hinted.mozHasAudio;
  if (hinted.audioTracks) return hinted.audioTracks.length > 0;
  if (file.size > AUDIO_PROBE_MAX_BYTES) return true;

  try {
    // The context's own rate and length are irrelevant; only its decoder is
    // being used, and it returns the file's own rate and duration.
    const context = new OfflineAudioContext({ numberOfChannels: 1, length: 128, sampleRate: 48000 });
    const buffer = await context.decodeAudioData(await file.arrayBuffer());
    return hasSignal(buffer);
  } catch {
    // Nothing decodable, which for every browser tested means no audio track.
    return false;
  }
}

/** Sampled rather than exhaustive: enough points to catch a real signal cheaply. */
function hasSignal(buffer: AudioBuffer): boolean {
  const stride = Math.max(1, Math.floor(buffer.length / 200_000));
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i += stride) {
      if (Math.abs(data[i] ?? 0) > SILENCE_PEAK) return true;
    }
  }
  return false;
}

const POSTER_WIDTH = 320;

/** The element is returned alongside, because the audio probe reuses it. */
function loadMetadata(url: string): Promise<{ probe: Probe; element: HTMLVideoElement }> {
  return new Promise((resolve, reject) => {
    const el = document.createElement("video");
    el.preload = "metadata";
    el.muted = true;
    el.src = url;

    const fail = () =>
      reject(new Error("Your browser could not open this file. Try MP4, WebM, MOV, MP3 or WAV."));
    el.onerror = fail;

    el.onloadedmetadata = () => {
      const hasVideo = el.videoWidth > 0 && el.videoHeight > 0;
      const base: Probe = {
        duration: el.duration,
        width: el.videoWidth,
        height: el.videoHeight,
        hasVideo,
      };
      if (!hasVideo) {
        resolve({ probe: base, element: el });
        return;
      }
      // A poster frame for the project list. Seeking a little way in avoids the
      // black first frame most footage starts on.
      el.currentTime = Math.min(el.duration * 0.1, 1);
      el.onseeked = () => {
        try {
          const scale = POSTER_WIDTH / el.videoWidth;
          const canvas = document.createElement("canvas");
          canvas.width = POSTER_WIDTH;
          canvas.height = Math.round(el.videoHeight * scale);
          canvas.getContext("2d")?.drawImage(el, 0, 0, canvas.width, canvas.height);
          resolve({ probe: { ...base, poster: canvas.toDataURL("image/jpeg", 0.6) }, element: el });
        } catch {
          resolve({ probe: base, element: el });
        }
      };
    };
  });
}

interface AudioProbe extends HTMLVideoElement {
  mozHasAudio?: boolean;
  webkitAudioDecodedByteCount?: number;
  audioTracks?: { length: number };
}

/**
 * Try to get a handle for a dropped file.
 *
 * Only a handle survives a reload, and only some browsers hand one out — and
 * only from the picker or from a drop. Asking here, at import, is the whole
 * reason we can later offer to reopen a project without asking for its files
 * again; a plain File is the fallback and works for everything except that.
 */
export async function handleFromDrop(item: DataTransferItem): Promise<FileSystemFileHandle | undefined> {
  const withHandle = item as DataTransferItem & {
    getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>;
  };
  if (typeof withHandle.getAsFileSystemHandle !== "function") return undefined;
  try {
    const handle = await withHandle.getAsFileSystemHandle();
    return handle && handle.kind === "file" ? (handle as FileSystemFileHandle) : undefined;
  } catch {
    return undefined;
  }
}

export const canPersistHandles = (): boolean =>
  typeof window !== "undefined" && "showOpenFilePicker" in window;
